import { useEffect, useRef, useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Cookie from 'universal-cookie'
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { tokenKey } from '../../api-client/client'
import {
  getConversation,
  getConversationKey,
  startConversation as startConversationApiFn,
  sendMessage as sendMessageApiFn,
  attachFile as attachFileApiFn,
  MarkMessagesAsSeen,
  getMyConversationsKey,
} from '../../api-client/chat'
import useNotifications from '../../hooks/use-notifications'
import { Mobile } from '../../components/media'
import File from './bubble/file/index'
import TimeWrapper from './bubble/time-wrapper'
import Text from './bubble/text'
import Modal from '../modal'
import Header from './header'
import Footer from './footer'
import { UserRole, UserType } from '../../api-client/enums'

const cookie = new Cookie()

export const ROLES = { sender: 0, receiver: 1 }

export default function Chat({ onClose, recipientId, initialTopic, vehicleId }) {
  const { refetchNotifications } = useNotifications()
  const chatRef = useRef()
  const signalrConRef = useRef()
  const progressBarRef = useRef()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState([])
  const [recipientInfo, setRecipientInfo] = useState({})
  const [error, setError] = useState()

  const { mutate: markAsSeen } = useMutation({
    mutationFn: MarkMessagesAsSeen,
    onSuccess: () => {
      queryClient.invalidateQueries(getMyConversationsKey)

      // give the notifications side a moment to refresh - this is hacky, but without a signalr-type push back from the notification side after chat tells notification that the conversation is seen, it's the only idea I have in my hat at the moment
      const timeoutRef = setTimeout(() => {
        refetchNotifications()
        clearTimeout(timeoutRef)
      }, 500)
    },
  })
  const { mutate: startConversation } = useMutation(startConversationApiFn)
  const { mutate: sendMessage } = useMutation(sendMessageApiFn)
  const { mutate: attachFile } = useMutation(attachFileApiFn)

  const setUpSignalRConnection = useCallback(
    async (conversationId) => {
      const connection = new HubConnectionBuilder()
        .withUrl(process.env.NEXT_PUBLIC_SIGNALR_CHAT_DOMAIN, {
          accessTokenFactory: () => cookie.get(tokenKey),
        })
        .withAutomaticReconnect()
        .build()

      connection.on('ReceiveMessage', (msg) => {
        setMessages((prev) => [...prev, msg])

        markAsSeen({ conversationId })
      })

      try {
        await connection.start()
      } catch (err) {
        alert("SignalR couln't connect")
      }

      if (connection.state === HubConnectionState.Connected) {
        connection.invoke('JoinConversation', { conversationId })

        markAsSeen({ conversationId })
      }

      signalrConRef.current = connection

      return connection
    },
    [markAsSeen],
  )

  const { data } = useQuery({
    queryFn: () => getConversation({ userId: recipientId, vehicleId, returnVerifiedDealer: true }),
    queryKey: [getConversationKey, recipientId],
    onSuccess: (data) => {
      let msgTopic = !!data.topicDetails ? data.topicDetails.topic ?? initialTopic : initialTopic
      const recipient = data.users.find(({ userId }) => userId === recipientId) || {}
      const isRecipientDealer = recipient.userData?.userType === UserType.Dealer
      const isRecipientAdmin = recipient.role === UserRole.Admin
      const isRecipientConcierge = recipient.role === UserRole.Concierge

      setRecipientInfo({
        topic: msgTopic,
        ...recipient,
        isDealer: isRecipientDealer,
        isAdmin: isRecipientAdmin,
        isConcierge: isRecipientConcierge,
        displayName: recipient.firstName,
      })

      if (data.messages) {
        setMessages(data.messages)
        setUpSignalRConnection(data.conversationId)
      }
    },
  })

  const handleSend = (message, file) => {
    if (data.messages === null) {
      const messageObj = {
        userId: recipientId,
        topic: initialTopic,
        vehicleId,
      }

      startConversation(messageObj, {
        onSuccess: (data) => {
          if (file) {
            attachFile(
              { file, conversationId: data.id, xhrProgressCb: handleUploadProgressChange },
              {
                onSuccess: (response) => {
                  handleUploadSuccess()

                  const msg = {
                    conversationId: data.id,
                    text: response.url,
                    isFile: true,
                    fileType: file.type,
                    fileSize: response.size,
                    fileKey: response.key,
                  }

                  sendMessage(msg, {
                    onSuccess: () => queryClient.invalidateQueries(getConversationKey),
                  })
                },
                onError: handleUploadError,
              },
            )
          } else {
            const msg = { conversationId: data.id, text: message.message }
            sendMessage(msg, {
              onSuccess: () => queryClient.invalidateQueries(getConversationKey),
            })
          }
        },
      })
    }

    if (signalrConRef.current?.state === HubConnectionState.Connected) {
      const messageObj = { conversationId: data.conversationId, text: message.message }

      if (message.isFile) {
        messageObj.isFile = true
        messageObj.fileType = message.fileType
        messageObj.fileSize = message.fileSize
        messageObj.fileKey = message.key
      }

      signalrConRef.current.invoke('SendMessage', messageObj)
    }
  }

  const animateProgressBarOut = () => {
    const slideUptimerRef = setTimeout(() => {
      progressBarRef.current.style.height = '0rem'

      clearInterval(slideUptimerRef)
    }, 1000)

    const nullifyElementTimerRef = setTimeout(() => {
      progressBarRef.current.style.transition = 'none'
      progressBarRef.current.style.width = 0
      progressBarRef.current.style.height = '0.8rem'
      progressBarRef.current.style.background = `var(--primary-color)`
      progressBarRef.current.style.transition = 'width: 0.5s, height: 0.5s'

      clearInterval(nullifyElementTimerRef)
    }, 1200)
  }

  const handleUploadProgressChange = (progress) => {
    if (error) setError('')

    const progressPercentage = ((progress.loaded / progress.total) * 100).toFixed(2)

    progressBarRef.current.style.width = `${progressPercentage}%`
  }

  const handleUploadSuccess = () => {
    const timerRef = setTimeout(() => {
      progressBarRef.current.style.background = `var(--success-color)`

      clearTimeout(timerRef)
    }, 500)

    animateProgressBarOut()
  }
  const handleUploadError = () => {
    const timerRef = setTimeout(() => {
      progressBarRef.current.style.background = `var(--alert-color)`

      clearTimeout(timerRef)
    }, 500)

    animateProgressBarOut()
  }

  useEffect(() => {
    if (messages.length > 0) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    return () => signalrConRef.current?.stop()
  }, [setUpSignalRConnection])

  return (
    <Modal onClose={onClose} hideCloseBtn>
      <div className='wrapper flex flexDirectionColumn justifyBetween'>
        <div className='progressBar' ref={progressBarRef} />
        <Mobile>
          <button className='flatBtn backToAccount' onClick={onClose}>
            &#60; Back to Account
          </button>
        </Mobile>
        <Header data={recipientInfo} onClose={onClose} errorMessage={error} />

        <div className='chatBubbleWrapper' ref={chatRef}>
          {messages.map(({ id, sendDate, receiverId, fileInfo, text }) => (
            <TimeWrapper
              key={id}
              date={sendDate}
              role={receiverId === recipientId ? ROLES.sender : ROLES.receiver}
            >
              {fileInfo.isFile ? (
                <File {...fileInfo} url={text} fileKey={`${data.conversationId}/${fileInfo.key}`} />
              ) : (
                <Text value={text} />
              )}
            </TimeWrapper>
          ))}
        </div>

        <Footer
          onSend={handleSend}
          conversationId={data?.conversationId}
          messages={data?.messages}
          onUploadProgressChange={handleUploadProgressChange}
          onUploadSuccess={handleUploadSuccess}
          onUploadError={handleUploadError}
          setSizeError={setError}
        />
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 80rem;
          height: 67rem;
          padding: 1.5rem 1.9rem;
          background: #fff;

          .backToAccount {
            color: var(--secondary-text-color);
            width: 100%;
            padding: 1rem 0 1.8rem;
            text-align: left;
            font-size: 1.2rem;
            font-weight: 700;
          }

          .progressBar {
            position: absolute;
            top: 0;
            left: 0;
            height: 0.8rem;
            width: 0;
            background: var(--primary-color);
            transition: width 0.3s, height 0.2s;
          }
        }

        .chatBubbleWrapper {
          max-height: 100%;
          height: 100%;
          padding: 0.5rem 1.1rem 0.5rem 0;
          overflow-y: auto;

          ::-webkit-scrollbar {
            width: 0.6rem;
          }

          ::-webkit-scrollbar-track {
            background: #f5f6f9;
          }

          ::-webkit-scrollbar-thumb {
            background: #a9bfff;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: var(--primary-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
          }
        }
      `}</style>
    </Modal>
  )
}
